/**
 * Step 1: Delivery type classification (STRICT + legacy parity).
 * EXPRESS if: (tags include "express" AND "city_<normalizedUserCity>") OR SkippCartDeliveryRules same-city match (city_* tag aligns with user city; no express tag required).
 * NEXT_DAY otherwise. Depends on skipp-cart-delivery-rules.js for legacy local detection.
 */
(function (global) {
  const EXPRESS = 'EXPRESS';
  const NEXT_DAY = 'NEXT_DAY';

  /**
   * Brand / vendor string match (cart line vendor ↔ metaobject brand_name).
   */
  function normalize(str) {
    return (str || '').trim().toLowerCase();
  }

  /**
   * Per spec: lowercase, no spaces (e.g. "Bangalore" → "bangalore", "New York" → "newyork").
   */
  function normalizeCityForExpressTag(userCity) {
    return String(userCity || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
  }

  /**
   * Raw user city string for normalization (prefers explicit slug, then location storage).
   */
  function resolveUserCityString() {
    if (typeof global.__SKIPP_USER_CITY_SLUG__ === 'string' && global.__SKIPP_USER_CITY_SLUG__.trim()) {
      return global.__SKIPP_USER_CITY_SLUG__.trim();
    }
    try {
      const raw = localStorage.getItem('userLocation');
      if (raw) {
        const j = JSON.parse(raw);
        const c = j.cityName || j.city || '';
        if (c) return String(c).trim();
      }
    } catch {
      /* ignore */
    }
    const loc = global.multiVendorCart?.userLocation;
    const c2 = loc?.cityName || loc?.city;
    if (c2) return String(c2).trim();
    return '';
  }

  /**
   * Normalize tags from a cart line item (Shopify /cart.js may omit tags; pass them when available).
   */
  function parseItemTags(item) {
    if (!item || item.tags == null) return [];
    if (Array.isArray(item.tags)) {
      return item.tags.map((t) => String(t).trim()).filter(Boolean);
    }
    if (typeof item.tags === 'string') {
      return item.tags
        .split(/[|,]/)
        .map((t) => t.trim())
        .filter(Boolean);
    }
    return [];
  }

  function parsePipeDelimitedTags(attr) {
    if (!attr) return [];
    return String(attr)
      .split('|')
      .map((t) => t.trim())
      .filter(Boolean);
  }

  /**
   * Map line item key → tags from cart row markup (data-product-tags).
   */
  function buildKeyToTagsFromDom() {
    const map = {};
    if (typeof document === 'undefined') return map;
    document.querySelectorAll('tr.cart-item[data-cart-line-key]').forEach((row) => {
      const key = row.getAttribute('data-cart-line-key');
      if (!key) return;
      map[key] = parsePipeDelimitedTags(row.getAttribute('data-product-tags'));
    });
    return map;
  }

  /**
   * @param {object} item
   * @param {Record<string, string[]>} domTagsByKey
   */
  function itemWithResolvedTags(item, domTagsByKey) {
    const fromApi = parseItemTags(item);
    if (fromApi.length) return { ...item, tags: fromApi };
    const fromDom = item?.key && domTagsByKey[item.key] ? domTagsByKey[item.key] : [];
    return { ...item, tags: fromDom };
  }

  /**
   * @param {{ tags?: string[] | string, vendor?: string }} item
   * @param {string} userCity
   * @returns {"EXPRESS" | "NEXT_DAY"}
   */
  function getDeliveryType(item, userCity) {
    const tagsRaw = parseItemTags(item) || [];
    const tagsLower = tagsRaw.map((t) => String(t).trim().toLowerCase());
    const normalizedCity = normalizeCityForExpressTag(userCity);
    const R = global.SkippCartDeliveryRules;

    if (
      R &&
      typeof R.getUserCitySlug === 'function' &&
      typeof R.isExpressProduct === 'function'
    ) {
      const citySlug = R.getUserCitySlug();
      if (citySlug && R.isExpressProduct(tagsRaw, citySlug)) return EXPRESS;
    }

    const strictExpress =
      normalizedCity.length > 0 &&
      tagsLower.includes('express') &&
      tagsLower.includes(`city_${normalizedCity}`);

    let localCityMatch = false;
    if (
      R &&
      typeof R.isLocalCityCartProduct === 'function' &&
      typeof R.getUserCitySlug === 'function'
    ) {
      const citySlug = R.getUserCitySlug();
      if (citySlug) {
        localCityMatch = R.isLocalCityCartProduct(tagsRaw, citySlug);
      }
    }

    return strictExpress || localCityMatch ? EXPRESS : NEXT_DAY;
  }

  /**
   * @param {{ items?: object[] }} cart
   * @param {string} userCity
   * @param {{ silent?: boolean }} [options]
   * @returns {{ cartAnalysis: object[], hasExpress: boolean, hasOnlyNextDay: boolean }}
   */
  function analyzeCart(cart, userCity, options) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const city = userCity === undefined || userCity === null ? resolveUserCityString() : String(userCity);
    const domTagsByKey = buildKeyToTagsFromDom();

    const cartAnalysis = items.map((item) => {
      const enriched = itemWithResolvedTags(item, domTagsByKey);
      return {
        id: item.id,
        key: item.key,
        vendor: item.vendor,
        deliveryType: getDeliveryType(enriched, city),
      };
    });

    const hasExpress = cartAnalysis.some((i) => i.deliveryType === EXPRESS);
    const hasOnlyNextDay = cartAnalysis.length > 0 && cartAnalysis.every((i) => i.deliveryType === NEXT_DAY);

    if (!options || !options.silent) {
      console.log('Cart Analysis:', cartAnalysis);
      console.log('Has Express:', hasExpress);
      console.log('Only Next Day:', hasOnlyNextDay);
    }

    return { cartAnalysis, hasExpress, hasOnlyNextDay };
  }

  async function runClassificationFromFetch() {
    const userCity = resolveUserCityString();
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const cart = await res.json();
      return analyzeCart(cart, userCity);
    } catch {
      return null;
    }
  }

  function runClassificationFromCartPayload(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    return analyzeCart(cart, resolveUserCityString());
  }

  global.SkippDeliveryClassification = {
    EXPRESS,
    NEXT_DAY,
    normalize,
    normalizeCityForExpressTag,
    resolveUserCityString,
    parseItemTags,
    parsePipeDelimitedTags,
    buildKeyToTagsFromDom,
    itemWithResolvedTags,
    getDeliveryType,
    analyzeCart,
    runClassificationFromFetch,
    runClassificationFromCartPayload,
  };
})(typeof window !== 'undefined' ? window : globalThis);
